import { useEffect, useMemo, useState } from 'react';
import DataTable from '@/components/DataTable.jsx';
import { StatTile, StatGrid } from '../manager/StatTiles.jsx';
import { api, ApiError } from '@/lib/api.js';
import './PlayerArchiveTab.css';

const num = (key, label, title) => ({ key, label, title: title ?? label, numeric: true });

/* Shared shape across the home/away, season and opposition splits. */
const splitColumns = (firstKey, firstLabel) => [
	{ key: firstKey, label: firstLabel, strong: true },
	num('matches', 'Apps', 'Matches played'),
	{ ...num('points', 'Points', 'Total points'), defaultSort: 'desc' },
	num('avg_points', 'PPG', 'Points per game'),
	num('goals', 'G', 'Goals'),
	num('assists', 'A', 'Assists'),
	num('clean_sheets', 'CS', 'Clean sheets'),
	num('double_digit_hauls', 'Hauls', 'Double-digit hauls'),
	num('hattricks', 'Hat-tricks'),
	num('minutes', 'Mins', 'Minutes played'),
];

/* The tab opened on M.Salah in the AngularJS version (controllers.js:646),
 * so the panel shows something useful before a search. Falls back to the
 * first name in the list if he is ever absent from it. */
const DEFAULT_PLAYER = 'M.Salah';

export default function PlayerArchiveTab() {
	const [names, setNames] = useState([]);
	const [selected, setSelected] = useState(DEFAULT_PLAYER);
	const [data, setData] = useState(null);
	const [status, setStatus] = useState('loading');
	const [error, setError] = useState(null);

	useEffect(() => {
		api
			.get('/api/history/player/')
			.then((list) => {
				const all = Array.isArray(list) ? list : [];
				setNames(all);
				if (all.length && !all.includes(DEFAULT_PLAYER)) setSelected(all[0]);
			})
			.catch(() => setNames([]));
	}, []);

	useEffect(() => {
		if (!selected) return undefined;
		let cancelled = false;
		setStatus('loading');
		setError(null);
		api
			.get(`/api/history/player/${encodeURIComponent(selected)}`)
			.then((payload) => {
				if (cancelled) return;
				if (!payload?.matches?.length) {
					setError('No match history recorded for that player.');
					setStatus('error');
					return;
				}
				setData(payload);
				setStatus('ready');
			})
			.catch((err) => {
				if (cancelled) return;
				setError(err instanceof ApiError ? err.message : 'Could not load that player.');
				setStatus('error');
			});
		return () => {
			cancelled = true;
		};
	}, [selected]);

	const agg = data?.aggregate_stats;

	/* home and away partition every appearance. `neither` is not a third
	 * venue — it belongs to the opposition-status axis below, alongside
	 * promoted and relegated, which partition the same appearances again. */
	const venueRows = useMemo(() => {
		if (!agg) return [];
		return [
			{ venue: 'Home', ...agg.home },
			{ venue: 'Away', ...agg.away },
		];
	}, [agg]);

	const oppositionStatusRows = useMemo(() => {
		if (!agg) return [];
		return [
			{ status: 'Established', ...agg.neither },
			{ status: 'Promoted', ...agg.promoted },
			{ status: 'Relegated', ...agg.relegated },
		].filter((r) => r.matches);
	}, [agg]);

	const oppositionRows = agg?.top_opposition?.by_points ?? [];

	return (
		<div className="player-archive">
			<div className="player-archive__search">
				<label className="field__label" htmlFor="player-select">
					Player
				</label>
				<input
					id="player-select"
					className="field__input"
					list="player-names"
					value={selected}
					onChange={(e) => setSelected(e.target.value)}
					placeholder={`Search ${names.length.toLocaleString()} players…`}
				/>
				{/* A datalist rather than select2: native typeahead, no dependency,
				    and it stays usable with 2,485 options. */}
				<datalist id="player-names">
					{names.map((n) => (
						<option key={n} value={n} />
					))}
				</datalist>
			</div>

			{status === 'loading' && (
				<div className="tab-loading" role="status">
					<span className="spinner" aria-hidden="true" />
					<span>Loading {selected}…</span>
				</div>
			)}

			{status === 'error' && (
				<div className="notice notice--error" role="alert">
					<strong>Could not load that player.</strong>
					<span>{error}</span>
				</div>
			)}

			{status === 'ready' && agg && (
				<>
					<section>
						<h3 className="section-title">Career</h3>
						<StatGrid label="Career totals">
							<StatTile label="Appearances" value={totals(agg, 'matches')} />
							<StatTile label="Points" value={totals(agg, 'points')} />
							<StatTile label="Goals" value={totals(agg, 'goals')} />
							<StatTile label="Assists" value={totals(agg, 'assists')} />
							<StatTile label="Clean sheets" value={totals(agg, 'clean_sheets')} />
							<StatTile label="Hauls" value={totals(agg, 'double_digit_hauls')} />
							<StatTile label="Hat-tricks" value={totals(agg, 'hattricks')} />
							<StatTile label="Seasons" value={agg.seasons?.length} />
						</StatGrid>
					</section>

					<section>
						<h3 className="section-title">By season</h3>
						<DataTable
							columns={splitColumns('season', 'Season')}
							rows={agg.seasons ?? []}
							rowKey={(r) => r.season}
							filterable={false}
						/>
					</section>

					<section>
						<h3 className="section-title">Home and away</h3>
						<DataTable
							columns={splitColumns('venue', 'Venue')}
							rows={venueRows}
							rowKey={(r) => r.venue}
							filterable={false}
						/>
					</section>

					{oppositionStatusRows.length > 0 && (
						<section>
							<h3 className="section-title">By opposition status</h3>
							<DataTable
								columns={splitColumns('status', 'Opposition')}
								rows={oppositionStatusRows}
								rowKey={(r) => r.status}
								filterable={false}
							/>
						</section>
					)}

					{oppositionRows.length > 0 && (
						<section>
							<h3 className="section-title">Best opposition</h3>
							<DataTable
								columns={splitColumns('team', 'Opponent')}
								rows={oppositionRows}
								rowKey={(r) => r.team}
								filterable={false}
							/>
						</section>
					)}

					{agg.phases?.length > 0 && (
						<section>
							<h3 className="section-title">By phase of season</h3>
							<DataTable
								columns={[
									{ key: 'phase', label: 'Gameweeks', strong: true },
									num('matches', 'Apps', 'Matches played'),
									{ ...num('points', 'Points'), defaultSort: 'desc' },
									num('ppg', 'PPG', 'Points per game'),
									num('goals', 'G', 'Goals'),
									num('goals_per_game', 'G/game', 'Goals per game'),
									num('assists', 'A', 'Assists'),
									num('assists_per_game', 'A/game', 'Assists per game'),
									num('minutes', 'Mins', 'Minutes played'),
								]}
								rows={agg.phases}
								rowKey={(r) => r.phase}
								filterable={false}
							/>
						</section>
					)}

					<section>
						<h3 className="section-title">Match log</h3>
						{/* Aggregates count appearances; the log also lists matches in
						    the squad with no minutes played. */}
						<p className="section-note">
							Every matchday squad inclusion, including matches with no minutes
							played.
						</p>
						<DataTable
							columns={matchColumns}
							rows={data.matches}
							rowKey={(r, i) => `${r.kickoff_time ?? r.round}-${i}`}
							filterPlaceholder="Filter matches by opponent or team"
							maxHeight="60vh"
							hideEmptyColumns
						/>
					</section>
				</>
			)}
		</div>
	);
}

/* Kickoff times are ISO strings, so the default string sort is already
 * chronological; only the display is reformatted. */
const kickoffFormat = new Intl.DateTimeFormat('en-GB', {
	weekday: 'short',
	day: 'numeric',
	month: 'short',
	year: 'numeric',
	hour: '2-digit',
	minute: '2-digit',
	timeZone: 'UTC',
});

const matchColumns = [
	{
		key: 'kickoff_time',
		label: 'Kickoff',
		title: 'Kickoff time (UTC)',
		defaultSort: 'desc',
		render: (v) => (v ? kickoffFormat.format(new Date(v)) : '—'),
	},
	num('round', 'GW', 'Gameweek'),
	{ key: 'team_name', label: 'Team', strong: true },
	{
		key: 'opponent_team_name',
		label: 'Fixture',
		title: 'Opponent, venue and final score',
		render: (opponent, row) => {
			const venue = row.was_home ? 'H' : 'A';
			const score =
				row.team_h_score == null || row.team_a_score == null
					? ''
					: ` ${row.team_h_score}–${row.team_a_score}`;
			return `${opponent} (${venue})${score}`;
		},
	},
	num('total_points', 'Pts', 'Points'),
	num('minutes', 'MP', 'Minutes played'),
	num('goals_scored', 'G', 'Goals scored'),
	num('assists', 'A', 'Assists'),
	num('clean_sheets', 'CS', 'Clean sheets'),
	num('bonus', 'Bonus', 'Bonus points'),
	num('bps', 'BPS', 'Bonus points system'),
	/* `value` is in tenths of a million. */
	{ ...num('value', 'Cost', 'Cost at the time'), render: (v) => `£${v / 10}` },
	{
		...num('selected', 'TSB', 'Teams selected by'),
		render: (v) => (v == null ? v : v.toLocaleString()),
	},
	num('goals_conceded', 'GC', 'Goals conceded'),
	num('own_goals', 'OG', 'Own goals'),
	num('penalties_saved', 'PS', 'Penalties saved'),
	num('penalties_missed', 'PM', 'Penalties missed'),
	num('yellow_cards', 'YC', 'Yellow cards'),
	num('red_cards', 'RC', 'Red cards'),
	num('saves', 'S', 'Saves'),
	num('ict_index', 'ICT', 'ICT index'),
	num('expected_goals', 'xG', 'Expected goals'),
	num('expected_assists', 'xA', 'Expected assists'),
];

/* Home and away partition every appearance, so summing just those two gives
 * the career total. Including `neither` would double-count: it is part of a
 * separate promoted/relegated/established split over the same matches. */
function totals(agg, key) {
	return ['home', 'away'].reduce((sum, venue) => sum + (agg[venue]?.[key] ?? 0), 0);
}
